import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import api, { pusher } from "../../services/api";
import { format } from "date-fns";
import { Clock, Package, } from "lucide-react";
import { generateInvoice } from "../../utils/invoiceGenerator";
import newOrderSound from "../../assets/audios/new-order.mp3";
import cancelledSound from "../../assets/audios/cancelled.mp3";
import OrderDetailsModal from "../Modals/OrderDetailsModal";

// Sound Constants
const NEW_ORDER_SOUND_URL = newOrderSound;
const CANCELLED_SOUND_URL = cancelledSound;

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const limit = 20;

  // Use a ref to track processed order IDs to avoid re-renders and stale closures
  // Initialize from LocalStorage to prevent sound on page reload
  const processedOrderIds = useRef(new Set());
  // Track alerted scheduled orders
  const processedScheduledIds = useRef(new Set());
  // Track previous statuses to detect changes (e.g. cancellations)
  const previousOrderStatuses = useRef({});

  const newOrderAudioRef = useRef(new Audio(NEW_ORDER_SOUND_URL));
  const cancelledAudioRef = useRef(new Audio(CANCELLED_SOUND_URL));
  const soundTimeoutRef = useRef(null); // Ref to clear timeout manually

  const stopAllSounds = useCallback(() => {
    // Stop New Order Sound
    newOrderAudioRef.current.pause();
    newOrderAudioRef.current.currentTime = 0;

    // Stop Cancelled Sound
    cancelledAudioRef.current.pause();
    cancelledAudioRef.current.currentTime = 0;

    // Clear any pending timeout
    if (soundTimeoutRef.current) {
      clearTimeout(soundTimeoutRef.current);
      soundTimeoutRef.current = null;
    }
  }, []);

  const playNewOrderSound = useCallback(() => {
    stopAllSounds(); // Stop any other playing sound first

    newOrderAudioRef.current.currentTime = 0;
    newOrderAudioRef.current.loop = true;

    const playPromise = newOrderAudioRef.current.play();

    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.log("Audio playback failed:", error);
      });
    }

    // Stop after 5 minutes (300,000 ms)
    soundTimeoutRef.current = setTimeout(() => {
      stopAllSounds();
    }, 300000);
  }, [stopAllSounds]);

  const playCancelledSound = useCallback(() => {
    stopAllSounds(); // Stop any other sound

    cancelledAudioRef.current.currentTime = 0;
    cancelledAudioRef.current.loop = true; // Loop for alarm importance

    const playPromise = cancelledAudioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.catch(error => console.log("Cancelled audio playback failed:", error));
    }

    // Stop after 8 seconds (8000 ms)
    soundTimeoutRef.current = setTimeout(() => {
      stopAllSounds();
    }, 8000);
  }, [stopAllSounds]);

  const processNotifications = useCallback((fetchedOrders, updateState = true) => {
    // Helper to check if scheduled order is due (within 30 mins)
    const isScheduledOrderDue = (order) => {
      if (!order.scheduled || !order.scheduled.date || !order.scheduled.time) return false;
      if (['delivered', 'cancelled', 'out_for_delivery'].includes(order.status)) return false;

      try {
        const scheduledDate = new Date(order.scheduled.date);
        const timeString = order.scheduled.time;
        let hours = 0; let minutes = 0;
        const timeParts = timeString.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (timeParts) {
          hours = parseInt(timeParts[1], 10);
          minutes = parseInt(timeParts[2], 10);
          const period = timeParts[3];
          if (period) {
            if (period.toUpperCase() === 'PM' && hours < 12) hours += 12;
            if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
          }
        }
        scheduledDate.setHours(hours, minutes, 0, 0);
        const now = new Date();
        const diffInMinutes = (scheduledDate - now) / (1000 * 60);
        return diffInMinutes <= 30;
      } catch (e) { return false; }
    };

    const newOrderIds = [];
    let hasCancelledOrder = false;
    let hasDueScheduledOrder = false;

    fetchedOrders.forEach(order => {
      if (!processedOrderIds.current.has(order.orderId)) {
        newOrderIds.push(order.orderId);
        processedOrderIds.current.add(order.orderId);
      }
      if (isScheduledOrderDue(order) && !processedScheduledIds.current.has(order.orderId)) {
        hasDueScheduledOrder = true;
        processedScheduledIds.current.add(order.orderId);
      }
      const prevStatus = previousOrderStatuses.current[order.orderId];
      if (order.status === 'cancelled' && prevStatus && prevStatus !== 'cancelled') {
        hasCancelledOrder = true;
      }
      previousOrderStatuses.current[order.orderId] = order.status;
    });

    const isInitialLoad = (fetchedOrders.length > 0 && newOrderIds.length === fetchedOrders.length);
    if (!isInitialLoad) {
      if (newOrderIds.length > 0) {
        playNewOrderSound();
        toast.success("New Order Received! 🔔");
      } else if (hasDueScheduledOrder) {
        playNewOrderSound();
        toast.success("Scheduled Order is Due Soon! ⏰");
      }
      if (hasCancelledOrder) {
        toast.error("Order Cancelled! ⚠️");
        playCancelledSound();
      }
    }

    if (newOrderIds.length > 0) localStorage.setItem('processedOrderIds', JSON.stringify([...processedOrderIds.current]));
    if (hasDueScheduledOrder) localStorage.setItem('processedScheduledIds', JSON.stringify([...processedScheduledIds.current]));

    if (updateState) {
      // Sort for state display
      const displayOrders = [...fetchedOrders].sort((a, b) => {
        const aDue = isScheduledOrderDue(a);
        const bDue = isScheduledOrderDue(b);
        if (aDue && !bDue) return -1;
        if (!aDue && bDue) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setOrders(displayOrders);
    }
  }, [playNewOrderSound, playCancelledSound]);

  const checkNewOrdersBackground = useCallback(async () => {
    try {
      const response = await api.get(`/orders/admin/all?page=1&limit=${limit}`);
      if (response.data.success) {
        processNotifications(response.data.data, false);
      }
    } catch (e) {
      console.error("Background check failed", e);
    }
  }, [processNotifications, limit]);

  const fetchOrders = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      let url = `/orders/admin/all?page=${page}&limit=${limit}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterDate) url += `&date=${filterDate}`;

      const response = await api.get(url);
      if (response.data.success) {
        setTotalPages(response.data.pages);
        setTotalOrders(response.data.total);
        processNotifications(response.data.data, true);
      }
    } catch (error) {
      console.error("Fetch Orders Error:", error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterDate, limit, processNotifications]);

  useEffect(() => {
    // Load state from local storage on mount
    try {
      const storedIds = JSON.parse(localStorage.getItem('processedOrderIds') || '[]');
      if (Array.isArray(storedIds)) {
        processedOrderIds.current = new Set(storedIds);
      }

      const storedScheduledIds = JSON.parse(localStorage.getItem('processedScheduledIds') || '[]');
      if (Array.isArray(storedScheduledIds)) {
        processedScheduledIds.current = new Set(storedScheduledIds);
      }
    } catch (e) {
      console.error("Failed to parse stored order IDs", e);
    }

    fetchOrders(currentPage);

    // Pusher for Real-time Admin Notifications
    const channel = pusher.subscribe("orders-channel");

    // Handle New Order
    channel.bind("new_order", (data) => {
      console.log("🔔 New order event received:", data);
      if (currentPage === 1 && filterStatus === 'all' && !filterDate) {
        fetchOrders(1);
      } else {
        // Background check to trigger sounds/toasts even if not on page 1
        checkNewOrdersBackground();
      }
    });

    // Handle Order Cancellation
    channel.bind("order_cancelled", (data) => {
      console.log("⚠️ Order cancellation event received:", data);
      fetchOrders(currentPage);
    });

    // Clear on unmount
    return () => {
      channel.unbind_all();
      pusher.unsubscribe("orders-channel");
      stopAllSounds();
    };
  }, [currentPage, filterStatus, filterDate, fetchOrders, checkNewOrdersBackground, stopAllSounds]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterDate]);

  const updateStatus = async (order, newStatus) => {
    if (newStatus === "confirmed") {
      generateInvoice(order);
    }
    try {
      const response = await api.patch(`/orders/${order.orderId}/status`, {
        status: newStatus,
      });
      if (response.data.success) {
        // Stop sound immediately on manual action
        stopAllSounds();

        toast.success("Order status updated");
        fetchOrders(); // Refresh list to ensure consistency
        if (selectedOrder && selectedOrder.orderId === order.orderId) {
          // Update the selected order as well locally to reflect the change immediately in modal if open
          setSelectedOrder(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      console.error("Update Status Error:", error);
      toast.error("Failed to update status");
    }
  };

  const updatePaymentStatus = async (order, newPaymentStatus) => {
    try {
      const response = await api.patch(`/orders/${order.orderId}/payment-status`, {
        paymentStatus: newPaymentStatus,
      });
      if (response.data.success) {
        toast.success("Payment status updated");
        fetchOrders();
        if (selectedOrder && selectedOrder.orderId === order.orderId) {
          setSelectedOrder(prev => ({ ...prev, paymentStatus: newPaymentStatus }));
        }
      }
    } catch (error) {
      console.error("Update Payment Status Error:", error);
      toast.error("Failed to update payment status");
    }
  };

  // We rely on server-side pagination/filtering now
  const filteredOrders = orders;

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    confirmed: "bg-blue-200 text-blue-800",
    preparing: "bg-purple-100 text-purple-800",
    out_for_delivery: "bg-indigo-100 text-indigo-800",
    delivered: "bg-gray-300 text-gray-800",
    cancelled: "bg-red-200 text-red-800",
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes blink-green {
          0% { background-color: #c4f3d2ff; }
          50% { background-color: #f5f5f5ff; }
          100% { background-color: #c4f3d2ff; }
        }
        .animate-blink-green {
          animation: blink-green 2s infinite;
        }
      `}</style>
      <div className="flex justify-between items-center px-2">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          Orders
          <span className="text-sm font-normal bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
            {totalOrders}
          </span>
        </h2>
        <div className="flex flex-wrap items-center gap-4">
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-sm"
          />
          <button
            onClick={fetchOrders}
            className="p-2 text-gray-600 hover:text-green-600 transition"
            title="Refresh Orders"
          >
            <Clock size={20} />
          </button>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="out_for_delivery">Out for Delivery</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Pagination UI */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * limit, totalOrders)}
                </span>{' '}
                of <span className="font-medium">{totalOrders}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  // Only show near current page
                  if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === pageNum
                          ? 'z-10 bg-green-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID / Date
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Advance Order
                </th>
                <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => {
                let rowClass = "hover:bg-gray-50 transition-colors duration-300";


                // Duplicate helper logic for simplicity in rendering or reuse if lifted up. 
                // Since I cannot lift up efficiently in this patch, I'll allow a minor inline check or just duplicate logic.
                // Ideally I'd move isScheduledOrderDue to component scope, but it was inside fetchOrders scope in my previous patch block.
                // Re-implementing simplified check here for class application.
                let isDue = false;
                if (order.scheduled && order.scheduled.date && order.scheduled.time) {
                  // Quick dirty check or parse again. 
                  // To avoid code duplication mess, let's just do a quick inline parse which is fast enough for 30 rows.
                  try {
                    const sDate = new Date(order.scheduled.date);
                    const tParts = order.scheduled.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
                    if (tParts) {
                      let h = parseInt(tParts[1]);
                      let m = parseInt(tParts[2]);
                      if (tParts[3]) {
                        if (tParts[3].toUpperCase() === 'PM' && h < 12) h += 12;
                        if (tParts[3].toUpperCase() === 'AM' && h === 12) h = 0;
                      }
                      sDate.setHours(h, m, 0, 0);
                      const minDiff = (sDate - new Date()) / 60000;
                      if (minDiff <= 30 && !['delivered', 'cancelled', 'out_for_delivery'].includes(order.status)) {
                        isDue = true;
                      }
                    }
                  } catch (e) { }
                }

                if (order.status === 'cancelled') {
                  rowClass = "bg-red-100 hover:bg-red-100";
                } else if (isDue) {
                  rowClass = "animate-blink-green hover:bg-green-100 bg-orange-100"; // Blinking + Base Orange hint
                } else if (order.scheduled) {
                  rowClass = "bg-orange-100 hover:bg-orange-100";
                } else if (order.status === 'pending') {
                  // Custom blinking class for new orders
                  rowClass = "animate-blink-green hover:bg-green-100";
                } else if (order.status === 'confirmed') {
                  rowClass = "bg-blue-100 hover:bg-blue-100";
                } else if (order.status === 'delivered') {
                  rowClass = "bg-gray-200 hover:bg-gray-200";
                }

                return (
                  <tr key={order._id} className={rowClass}>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">#{order.orderId}</div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(order.createdAt), "MMM d, h:mm a")}
                      </div>
                      {order.orderType === 'pickup' && (
                        <span className="mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-bold rounded uppercase inline-block">
                          Pickup
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.scheduled ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-orange-600 flex items-center gap-1">
                            <Clock size={14} />
                            {order.scheduled.time}
                          </span>
                          <span className="text-xs text-gray-500">
                            {format(new Date(order.scheduled.date), "MMM d, yyyy")}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-1 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="font-medium text-gray-900">{order.customerDetails?.name}</div>
                      <div className="text-xs">{order.customerDetails?.phone}</div>
                    </td>
                    <td className="px-1 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate">
                        {order.items.map(item => `${item.quantity}x ${item.name}`).join(", ")}
                      </div>
                      {order.items.length > 2 && (
                        <div className="text-xs text-green-600 mt-1">+{order.items.length - 2} more items</div>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{order.pricing?.total}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[order.status] || "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {order.status.replace(/_/g, " ").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {order.paymentStatus === 'paid' ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                          Paid
                        </span>
                      ) : (
                        <select
                          value={order.paymentStatus || 'pending'}
                          onChange={(e) => updatePaymentStatus(order, e.target.value)}
                          className="border border-red-300 bg-red-50 text-red-800 font-semibold rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                        >
                          <option value="pending" className="font-semibold text-red-800 bg-white">Unpaid</option>
                          <option value="paid" className="font-semibold text-green-800 bg-white">Paid</option>
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="text-green-600 hover:text-green-900 font-medium text-xs bg-green-50 px-3 py-1.5 rounded hover:bg-green-100 transition whitespace-nowrap"
                        >
                          Details
                        </button>
                        <select
                          value={order.status}
                          onChange={(e) => updateStatus(order, e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="preparing">Preparing</option>
                          <option value="out_for_delivery">Out for Delivery</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredOrders.length === 0 && (
          <div className="py-12 text-center text-gray-400 bg-gray-50">
            <Package className="mx-auto h-12 w-12 text-gray-300 mb-2" />
            <p>No orders found matching this filter.</p>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          selectedOrder={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          updateStatus={updateStatus}
          statusColors={statusColors}
          generateInvoice={generateInvoice}
        />
      )}
    </div>
  );
};


export default OrderManagement;
